import { useEffect, useState, useMemo, Fragment } from "react";
import { useParams, useLocation } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { getApiBase } from "../apiBase";
import { FiInfo, FiUser, FiMessageSquare, FiClock, FiCpu, FiMail, FiPhone } from "react-icons/fi";
import {
  FiLayers,
  FiAlertCircle,
  FiCheckCircle,
  FiCornerUpRight,
  FiBookOpen,
  FiSlash,
  FiAlertTriangle
} from "react-icons/fi";

export default function UserRecords() {
  const { tenantId, botId } = useParams();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [escalations, setEscalations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [statusFilter, setStatusFilter] = useState("new");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expandedSessions, setExpandedSessions] = useState({});
  const [sessionTurnCounts, setSessionTurnCounts] = useState({});
  const [escalatedTurn, setEscalatedTurn] = useState(null);
  const location = useLocation();

  useEffect(() => {
    fetchEscalations();
  }, [tenantId, botId]);

  useEffect(() => {
  if (!location.state?.escalationId) return;

  const found = escalations.find(
    e => e.escalation_id === location.state.escalationId
  );

  if (found) {
    setSelected(found);
    fetchConversation(found.session_id, found);

    setExpandedSessions(prev => ({
      ...prev,
      [found.session_id]: true
    }));
  }
}, [escalations, location.state]);

  const fetchTurnCount = async (sessionId) => {
    if (sessionTurnCounts[sessionId]) return;

    try {
      const res = await fetch(
        `${getApiBase()}/sessions/${sessionId}/stats?tenant_id=${tenantId}&bot_id=${botId}`
      );
      const json = await res.json();
      setSessionTurnCounts(prev => ({
        ...prev,
        [sessionId]: json.turns?.length || 0
      }));
    } catch (err) {
      console.error("Failed to fetch turn count", err);
    }
  };

  const fetchEscalations = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/api/escalations?tenant_id=${tenantId}${botId ? `&bot_id=${botId}` : ''}`);
      const json = await res.json();
      setEscalations(json.tickets || []);
    } catch (err) {
      console.error("Failed to load escalations", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSession = (sessionId) => {
    setExpandedSessions(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };

  const fetchConversation = async (sessionId, escalation) => {
    setLoadingConversation(true);
    try {
      const res = await fetch(`${getApiBase()}/sessions/${sessionId}/stats?tenant_id=${tenantId}&bot_id=${botId}`);
      const json = await res.json();
      const turns = json.turns || [];
      setConversation(turns);

      if (escalation) {
        const normalize = s => (s || "").toLowerCase().trim();
        const oq = normalize(escalation.original_query);
        let idx = turns.findIndex(t => normalize(t.conversation?.user_query) === oq);

        if (idx === -1) {
          idx = turns.findIndex(t =>
            normalize(t.conversation?.user_query).includes(oq) ||
            oq.includes(normalize(t.conversation?.user_query))
          );
        }

        if (idx === -1 && selected.created_at) {
          const escTime = new Date(selected.created_at).getTime();
          let closestIdx = -1;
          let smallestDiff = Infinity;

          turns.forEach((t, i) => {
            const tTime = new Date(t.created_at || t.conversation?.created_at || 0).getTime();
            const diff = Math.abs(tTime - escTime);
            if (diff < smallestDiff) {
              smallestDiff = diff;
              closestIdx = i;
            }
          });

          idx = closestIdx;
        }

        setEscalatedTurn(idx >= 0 ? idx + 1 : null);
      }
    } catch (err) {
      console.error("Failed to load conversation", err);
      setConversation([]);
    } finally {
      setLoadingConversation(false);
    }
  };

  const handleSelect = (e) => {
    setEscalatedTurn(null);
    setSelected(e);
    fetchConversation(e.session_id, e);
  };

  const filtered = useMemo(() => {
    return escalations.filter(e => {
      const s = statusFilter === "all" || e.status === statusFilter;
      const t = typeFilter === "all" || e.escalation_type === typeFilter;
      return s && t;
    });
  }, [escalations, statusFilter, typeFilter]);

  const groupedBySession = useMemo(() => {
    const map = {};

    filtered.forEach(e => {
      if (!map[e.session_id]) {
        map[e.session_id] = [];
      }
      map[e.session_id].push(e);
    });

    return Object.entries(map).map(([sessionId, items]) => {
      const sessionCreatedAt = items.reduce((earliest, item) => {
        const t = new Date(item.created_at);
        return !earliest || t < earliest ? t : earliest;
      }, null);

      return [sessionId, items, sessionCreatedAt];
    });
  }, [filtered]);

  useEffect(() => {
    groupedBySession.forEach(([sessionId]) => {
      fetchTurnCount(sessionId);
    });
  }, [groupedBySession]);

  const formatDateTime = d =>
    new Date(d).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });

  const formatAgo = d => formatDistanceToNow(toZonedTime(new Date(d), tz), { addSuffix: true });

  const total = escalations.length;
  const newCount = escalations.filter(e => e.status === "new").length;
  const resolved = escalations.filter(e => e.status === "resolved").length;
  const handoff = escalations.filter(e => e.escalation_type === "handoff").length;
  const kg = escalations.filter(e => e.escalation_type === "kb_gap").length;
  const oos = escalations.filter(e => e.escalation_type === "out_of_scope").length;

  const getTypeLabel = (type) => {
    switch (type) {
      case "handoff": return "Handoff";
      case "kb_gap": return "Knowledge Gap";
      case "out_of_scope": return "Out of Scope";
      default: return type.replace(/_/g, " ");
    }
  };

  return (
    <div className="p-6 bg-[#F8F4F2] min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-[#5B3A60]">Escalation Dashboard</h1>
              <p className="text-gray-600 mt-1">Review and manage user escalations</p>
            </div>
            <div className="flex gap-4">
              <div className="min-w-[160px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#5B3A60] focus:outline-none bg-white"
                >
                  <option value="all">All Statuses</option>
                  <option value="new">New</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              <div className="min-w-[160px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select
                  value={typeFilter}
                  onChange={e => setTypeFilter(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#5B3A60] focus:outline-none bg-white"
                >
                  <option value="all">All Types</option>
                  <option value="handoff">Handoff</option>
                  <option value="kb_gap">Knowledge Gap</option>
                  <option value="out_of_scope">Out of Scope</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 mb-6">
          <StatCard title="Total" value={total} icon={FiLayers} />
          <StatCard title="New" value={newCount} color="red" icon={FiAlertCircle} />
          <StatCard title="Resolved" value={resolved} color="green" icon={FiCheckCircle} />
          <StatCard title="Handoff" value={handoff} icon={FiCornerUpRight} />
          <StatCard title="Knowledge Gap" value={kg} icon={FiBookOpen} />
          <StatCard title="Out of Scope" value={oos} icon={FiSlash} />
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[#5B3A60] border-t-transparent"></div>
              <p className="mt-3">Loading escalations...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p>No escalations match your filters.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedBySession.map(([sessionId, items, sessionCreatedAt]) => {
                const isOpen = expandedSessions[sessionId];
                const first = items[0];
                const hasNew = items.some(i => i.status === "new");
                const turns = sessionTurnCounts[sessionId] || 0;
                const density = turns ? (items.length / turns) * 100 : 0;
                const isHighDensity = density >= 50;

                return (
                  <div
                    key={sessionId}
                    className="border border-gray-200 rounded-xl bg-white overflow-hidden"
                  >
                    <div
                      onClick={() => toggleSession(sessionId)}
                      className={`flex items-center justify-between px-6 py-4 cursor-pointer transition ${
                        isOpen ? "bg-[#F2EDF5]" : "hover:bg-[#FAF8FB]"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-2.5 h-12 rounded-full ${hasNew ? "bg-red-400" : "bg-emerald-400"}`} />
                        <div>
                          <div className="text-xs text-gray-400 uppercase tracking-widest">Session</div>
                          <div className="font-mono text-xs text-gray-700">{sessionId}</div>
                          <div className="text-xs text-gray-500">
                            User: <span className="text-gray-700 font-medium">{first.user_name || "Anonymous"}</span>
                          </div>
                          {sessionCreatedAt && (
                            <div className="text-[11px] text-gray-400 mt-0.5">
                              Started on {formatDateTime(sessionCreatedAt)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="text-right">
                          <div className="text-right">
                            <div className="text-[10px] text-gray-400 uppercase tracking-widest">
                              Escalations
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-lg font-bold ${
                                isHighDensity ? "text-red-700" : "text-[#5B3A60]"
                              }`}>
                                {items.length}
                              </span>
                              <span className="text-xs text-gray-600">
                                in {turns || "—"} turns
                              </span>
                              {turns > 0 && (
                                <span className={`ml-1 text-xs font-semibold ${
                                  isHighDensity ? "text-red-600" : "text-gray-500"
                                }`}>
                                  ({density.toFixed(0)}%)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={`text-xl transition-transform ${isOpen ? "rotate-90 text-[#5B3A60]" : "text-gray-400"}`}>
                          ▸
                        </div>
                      </div>
                    </div>
                    {isOpen && (
                      <div className="divide-y divide-gray-100">
                        {items.map(e => (
                          <div
                            key={e.escalation_id}
                            onClick={() => handleSelect(e)}
                            className="px-6 py-4 grid grid-cols-5 gap-4 hover:bg-[#FAF5FB] cursor-pointer transition"
                          >
                            <div>
                              <span className="inline-flex px-3 py-1 rounded-full bg-[#F1ECF5] text-[#5B3A60] text-xs font-medium">
                                {getTypeLabel(e.escalation_type)}
                              </span>
                            </div>
                            <div>
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                e.status === "new"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-emerald-100 text-emerald-700"
                              }`}>
                                {e.status.toUpperCase()}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatAgo(e.created_at)}
                            </div>
                            <div className="text-sm font-medium text-gray-800">
                              {e.user_name || "Anonymous"}
                            </div>
                            <div className="text-sm text-gray-700 line-clamp-4 break-words">
                              {e.original_query || "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {selected && (
          <EscalationModal
            selected={selected}
            conversation={conversation}
            escalatedTurn={escalatedTurn}
            loadingConversation={loadingConversation}
            onClose={() => setSelected(null)}
            onUpdateEscalation={(updated) => {
              setSelected(updated);
              setEscalations(prev =>
                prev.map(e => e.escalation_id === updated.escalation_id ? updated : e)
              );
            }}
          />
        )}
      </div>
    </div>
  );
}

const EscalationModal = ({ selected, conversation, escalatedTurn, loadingConversation, onClose, onUpdateEscalation }) => {
  const [idealAnswer, setIdealAnswer] = useState(selected.ideal_answer || "");
  const [escalationType, setEscalationType] = useState(selected.escalation_type);
  const [isSaving, setIsSaving] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState(
    selected.resolution_notes || ""
  );
  const showIdealAnswer = escalationType === "kb_gap";

const showFeedback =
  ["kb_gap", "handoff", "out_of_scope"].includes(escalationType);

  const hasUnsavedChanges =
  idealAnswer !== (selected.ideal_answer || "") ||
  escalationType !== selected.escalation_type ||
  resolutionNotes !== (selected.resolution_notes || "");

  const handleResolve = async () => {
  setIsResolving(true);
  try {
    const res = await fetch(
      `${getApiBase()}/api/escalations/${selected.escalation_id}/resolve`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resolution_notes: resolutionNotes,
          resolution_type: escalationType
        })
      }
    );

    if (res.ok) {
      const updated = {
        ...selected,
        status: "resolved",
        resolution_notes: resolutionNotes
      };
      onUpdateEscalation(updated);
    } else {
      throw new Error("Resolve failed");
    }
  } catch (err) {
    console.error("Resolve failed:", err);
    alert("Failed to mark as resolved");
  } finally {
    setIsResolving(false);
  }
};

  const handleSave = async () => {
  setIsSaving(true);
  try {
    const payload = {};

    if (idealAnswer !== (selected.ideal_answer || "")) {
      payload.ideal_answer = idealAnswer;
    }

    if (escalationType !== selected.escalation_type) {
      payload.escalation_type = escalationType;
    }

    if (resolutionNotes !== (selected.resolution_notes || "")) {
      payload.resolution_notes = resolutionNotes;
    }

    const res = await fetch(
      `${getApiBase()}/api/escalations/${selected.escalation_id}/save`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      }
    );

    if (res.ok) {
      const updated = { ...selected, ...payload };
      onUpdateEscalation(updated);
    } else {
      throw new Error("Save failed");
    }
  } catch (err) {
    console.error("Save failed:", err);
    alert("Failed to save changes");
  } finally {
    setIsSaving(false);
  }
};

  const getTypeLabel = (type) => {
    switch (type) {
      case "handoff": return "Handoff";
      case "kb_gap": return "Knowledge Gap";
      case "out_of_scope": return "Out of Scope";
      default: return type.replace(/_/g, " ");
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-black/40 to-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-6xl h-[90vh] rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.25)] overflow-hidden flex flex-col border border-white/30">
        <div className="bg-gradient-to-r from-[#F8F4F2] to-[#EFE7E1] px-6 py-4 flex justify-between items-center border-b border-[#E6DDD8]">
          <div className="flex items-center gap-4">
            <div className="text-xl font-bold text-[#5B3A60] tracking-wide">Escalation Details</div>
            <select
              value={escalationType}
              onChange={e => setEscalationType(e.target.value)}
              className="border border-[#E6DDD8] rounded-full px-4 py-1.5 text-sm bg-white shadow-sm hover:shadow focus:ring-2 focus:ring-[#5B3A60] focus:outline-none transition"
            >
              <option value="handoff">Handoff</option>
              <option value="out_of_scope">Out of Scope</option>
              <option value="kb_gap">Knowledge Gap</option>
            </select>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${
              selected.status === "new"
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}>
              {selected.status.toUpperCase()}
            </span>
          </div>
          <div className="flex gap-3">
            {selected.status === "new" && (
              <button
                onClick={handleResolve}
                disabled={isResolving}
                className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:to-emerald-600 text-white rounded-xl text-sm font-medium shadow-md transition disabled:opacity-70"
              >
                {isResolving ? "Resolving..." : "Mark Resolved"}
              </button>
            )}
            {hasUnsavedChanges && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-gradient-to-r from-[#5B3A60] to-[#6E4A75] hover:to-[#5B3A60] text-white rounded-xl text-sm font-medium shadow-md transition disabled:opacity-70"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-white shadow hover:bg-gray-100 flex items-center justify-center text-gray-600 transition"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="w-1/2 p-6 overflow-y-auto border-r border-[#E6DDD8] bg-gradient-to-b from-[#FAF8F5] to-[#F3EEE9]">
            <h3 className="text-sm font-semibold text-[#5B3A60] mb-4 tracking-wide">Conversation History</h3>
            {escalatedTurn && conversation[escalatedTurn - 1] && (
              <div className="mb-4 p-4 rounded-2xl border border-red-300 bg-red-50 shadow-sm">
                <div className="text-xs font-semibold text-red-700 mb-1">
                  Escalated Turn · Turn {escalatedTurn}
                </div>
                <div className="text-sm text-gray-800 mb-1">
                  <span className="font-medium">User:</span>{" "}
                  {conversation[escalatedTurn - 1].conversation.user_query || "—"}
                </div>
                <div className="text-sm text-gray-800">
                  <span className="font-medium">Bot:</span>{" "}
                  {conversation[escalatedTurn - 1].conversation.bot_response || "—"}
                </div>
              </div>
            )}

            {loadingConversation ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#5B3A60]"></div>
              </div>
            ) : conversation.length === 0 ? (
              <div className="text-gray-500 text-sm py-8 text-center">No conversation history</div>
            ) : (
              <div className="space-y-5">
                {conversation.map((t, index) => {
                  const turnNumber = index + 1;
                  const isEscalated = escalatedTurn === turnNumber;
                  return (
                    <div
                      key={t.message_id}
                      className={`space-y-2 p-2 rounded-xl ${
                        isEscalated ? "bg-red-50 border border-red-300" : ""
                      }`}
                    >
                      <div className="flex justify-start">
                        <div className="bg-gradient-to-br from-[#EDE7F0] to-[#E4DAEC] p-4 rounded-2xl max-w-[85%] shadow-sm">
                          <div className="text-xs text-[#5B3A60] font-medium mb-1">
                            Turn {turnNumber} · User
                          </div>
                          <div className="text-sm text-gray-800 leading-relaxed">
                            {t.conversation.user_query || "— No query"}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="bg-white p-4 rounded-2xl border border-[#E6DDD8] max-w-[85%] shadow-sm">
                          <div className="text-xs text-gray-600 font-medium mb-1">
                            Turn {turnNumber} · Bot
                          </div>
                          <div className="text-sm text-gray-800 leading-relaxed">
                            {t.conversation.bot_response || "— No response"}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="w-1/2 p-6 overflow-y-auto space-y-6 bg-gradient-to-b from-white to-[#FAF7F3]">
            <Section title="Escalation Info" icon={FiInfo}>
              <Info label="Session ID" value={selected.session_id} />
              <Info label="Bot ID" value={selected.bot_id} />
              <Info label="Created" value={new Date(selected.created_at).toLocaleString()} />
              <Info label="Escalated Turn" value={escalatedTurn ? `Turn ${escalatedTurn}` : "—"} />
              <Info label="Type" value={escalationType.replace(/_/g, " ")} />
              <Info label="Status" value={selected.status} />
            </Section>
            <Section title="User Information" icon={FiUser}>
              <div className="flex items-center gap-2">
                <FiUser size={14} className="text-gray-400" />
                <Info label="Name" value={selected.user_name} />
              </div>
              <div className="flex items-center gap-2">
                <FiPhone size={14} className="text-gray-400" />
                <Info label="Phone" value={selected.user_phone} />
              </div>
              <div className="flex items-center gap-2">
                <FiMail size={14} className="text-gray-400" />
                <Info label="Email" value={selected.user_email} />
              </div>
            </Section>
            <Section title="Context" icon={FiMessageSquare}>
              <Info label="Reason" value={selected.handoff_reason || selected.escalation_reason} />
              <Info label="Original Query" value={selected.original_query} />
            </Section>
            <Section title="AI Diagnosis" icon={FiCpu}>
              <Info label="Flow Type" value={selected.flow_type} />
              <Info label="Flow Confidence" value={selected.flow_confidence && `${(selected.flow_confidence * 100).toFixed(1)}%`} />
              <Info label="Retrieval Confidence" value={selected.retrieval_confidence} />
            </Section>
            {selected.reasoning_steps?.length > 0 && (
              <Section title="Pipeline Trace" icon={FiLayers}>
                <ul className="list-disc ml-5 space-y-1 text-xs text-gray-700">
                  {selected.reasoning_steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </Section>
            )}
            {/* IDEAL ANSWER */}
{showIdealAnswer && (
  <Section title="Ideal Answer" icon={FiCpu}>
    <textarea
      value={idealAnswer}
      onChange={e => setIdealAnswer(e.target.value)}
      rows={4}
      className="w-full border border-[#E6DDD8] rounded-xl p-3 text-sm focus:ring-2 focus:ring-[#5B3A60] focus:outline-none bg-white shadow-inner"
      placeholder="Provide the ideal answer for this escalation..."
    />
    <div className="text-xs text-gray-500 italic">
      {hasUnsavedChanges ? "Unsaved changes" : "All changes saved"}
    </div>
  </Section>
)}

{/* FEEDBACK BOX */}
{showFeedback && (
  <Section title="Feedback by Support Agent" icon={FiCheckCircle}>
    <textarea
      value={resolutionNotes}
      onChange={e => setResolutionNotes(e.target.value)}
      rows={4}
      disabled={selected.status === "resolved"}
      className={`w-full border border-[#E6DDD8] rounded-xl p-3 text-sm focus:ring-2 focus:ring-[#5B3A60] focus:outline-none shadow-inner ${
        selected.status === "resolved"
          ? "bg-gray-100 text-gray-600 cursor-not-allowed"
          : "bg-white"
      }`}
      placeholder="Add resolution notes before marking resolved..."
    />
  </Section>
)}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, color, icon: Icon }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex items-center gap-4 hover:shadow-md transition">
    <div className={`p-3 rounded-xl ${
      color === "red" ? "bg-red-50 text-red-600" :
      color === "green" ? "bg-emerald-50 text-emerald-600" :
      "bg-[#F3EEF5] text-[#5B3A60]"
    }`}>
      {Icon && <Icon size={20} />}
    </div>
    <div className="flex-1">
      <div className="text-xs text-gray-500 font-medium">{title}</div>
      <div className={`text-2xl font-bold ${
        color === "red" ? "text-red-600" :
        color === "green" ? "text-emerald-600" :
        "text-[#5B3A60]"
      }`}>
        {value}
      </div>
    </div>
  </div>
);

const Info = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm text-gray-800 leading-relaxed break-words">
      {value !== null && value !== undefined && value !== "" ? value : "—"}
    </span>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-[#F8F4F2] rounded-2xl border border-[#E6DDD8] p-5 shadow-sm space-y-3">
    <div className="flex items-center gap-2 text-xs font-semibold text-[#5B3A60] uppercase tracking-wide">
      {Icon && <Icon size={14} />}
      {title}
    </div>
    <div className="space-y-2 text-sm">
      {children}
    </div>
  </div>
);